import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  IconButton,
  Popover,
  Grid,
  Tooltip,
} from '@mui/material';
import { EmojiEmotions as EmojiIcon } from '@mui/icons-material';
import twemoji from 'twemoji';
import './EmojiPicker.css';

// Common encouraging emojis for teachers/parents
const EMOJI_CATEGORIES = {
  positive: ['😊', '😄', '😃', '😁', '🙂', '😉', '😎', '🤗'],
  celebration: ['🎉', '🎊', '🎈', '🎁', '🏆', '⭐', '🌟', '💫'],
  encouragement: ['👍', '👏', '💪', '🔥', '✨', '💯', '🎯', '🚀'],
  love: ['❤️', '💙', '💚', '💛', '🧡', '💜', '🤍', '🖤'],
  thumbs: ['👍', '👎', '✌️', '🤞', '🤝', '🙌', '👋', '🤚'],
};

// Flatten and deduplicate emojis
const ALL_EMOJIS = [
  ...new Set([
    ...EMOJI_CATEGORIES.positive,
    ...EMOJI_CATEGORIES.celebration,
    ...EMOJI_CATEGORIES.encouragement,
    ...EMOJI_CATEGORIES.love,
    ...EMOJI_CATEGORIES.thumbs,
  ])
];

const EmojiPicker = ({ onEmojiSelect, disabled = false }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const emojiContainerRef = useRef(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEmojiClick = (emoji) => {
    if (onEmojiSelect) {
      onEmojiSelect(emoji);
    }
    handleClose();
  };

  // Convert emoji to Twemoji SVG image when popover opens
  useEffect(() => {
    if (!open || !emojiContainerRef.current) return;
    
    let rafId1, rafId2;
    
    // Use double requestAnimationFrame to ensure DOM is fully rendered and painted
    rafId1 = requestAnimationFrame(() => {
      rafId2 = requestAnimationFrame(() => {
        if (emojiContainerRef.current) {
          // Parse all emoji characters in the container to Twemoji SVG images
          twemoji.parse(emojiContainerRef.current, {
            base: 'https://cdnjs.cloudflare.com/ajax/libs/twemoji/14.0.2/',
            folder: 'svg',
            ext: '.svg',
            className: 'twemoji-icon',
          });
        }
      });
    });
    
    return () => {
      if (rafId1) cancelAnimationFrame(rafId1);
      if (rafId2) cancelAnimationFrame(rafId2);
    };
  }, [open]);

  return (
    <>
      <Tooltip title="Add emoji">
        <IconButton
          onClick={handleClick}
          disabled={disabled}
          size="small"
          sx={{
            color: 'primary.main',
            opacity: disabled ? 0.5 : 1,
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <EmojiIcon />
        </IconButton>
      </Tooltip>
      
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        disablePortal={false}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        onClick={(e) => {
          // Prevent clicks inside popover from closing it
          e.stopPropagation();
        }}
        onMouseDown={(e) => {
          // Prevent mouse down from closing popover
          e.stopPropagation();
        }}
        PaperProps={{
          className: "emoji-picker-paper",
          onClick: (e) => e.stopPropagation(),
          onMouseDown: (e) => e.stopPropagation(),
          sx: {
            p: 1.5,
            maxWidth: 320,
            maxHeight: 400,
            overflowY: 'auto',
            backgroundColor: '#ffffff',
            zIndex: 1501,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
            // Explicitly prevent any filters or transforms that could cause blur
            filter: 'none',
            backdropFilter: 'none',
            transform: 'none',
            isolation: 'isolate',
          },
        }}
      >
        <Box sx={{ minWidth: 280 }} ref={emojiContainerRef} key={open ? 'open' : 'closed'}>
          {ALL_EMOJIS.length > 0 ? (
            <Grid container spacing={0.5}>
              {ALL_EMOJIS.map((emoji, index) => (
                <Grid item key={`emoji-${index}`}>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEmojiClick(emoji); // still pass native char to parent
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    sx={{
                      width: 44,
                      height: 44,
                      padding: 0,
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <span className="emoji-char">{emoji}</span>
                  </IconButton>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box sx={{ p: 2, textAlign: 'center' }}>No emojis available</Box>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default EmojiPicker;

